// @flow

import React, {Component} from 'react';
import CheckBox from '@pb/kit-component-checkbox';
import Info from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {Popover, PopoverHeader} from '@pb/kit-component-dropdown-v2';
import {shouldUpdate} from '@pb/kit-util-should-update';
import {layoutLevels} from '@pb/kit-stylus/config';

import {getCheckedState, updateCheckedIdsList, getLabel} from './utils';

import type {NodeProps} from '@pb/kit-component-tree-v2';

import type {CheckedTree, ViewType} from './typedef';

/**
 * Checkbox Tree Node props
 */
export type Props = {|
    /**
     * Component instance name
     */
    name: string,
    /**
     * Disabled state
     */
    disabledNode: ?string,
    /**
     * List of checked items
     */
    checkedIdsTree: CheckedTree,
    /**
     * Checked item ids
     */
    checkedIdsList: Array<string>,
    /**
     * Component layout level (Layout priority).
     */
    layoutLevel: $Values<typeof layoutLevels>,
    /**
     * Show explanation popovers
     */
    showExplanations: boolean,
    /**
     * Callback called if data changed
     */
    onChange: (checkedIdsList: Array<string>, id: string, checked: boolean) => void,
    /**
     * Function return an item and provides an interface to work with them
     */
    view: ?ViewType,
    ...NodeProps,
|};

type State = {|
    indeterminate: boolean,
    checked: boolean,
|};

export class CheckboxTreeNode extends Component<Props, State> {
    static defaultProps = {
        name: '',
        checkedIdsList: [],
        layoutLevel: layoutLevels.level8,
        showExplanations: false
    };

    state = getCheckedState({
        checkedIdsList: this.props.checkedIdsList,
        checkedIdsTree: this.props.checkedIdsTree,
        data: this.props.data
    });

    componentDidUpdate(prevProps: Props) {
        if (shouldUpdate(prevProps, this.props, ['checkedIdsTree', 'checkedIdsList'])) {
            const {checkedIdsList, checkedIdsTree, data} = this.props;
            this.setState(getCheckedState({checkedIdsList, checkedIdsTree, data}));
        }
    }

    onChange = () => {
        const checked = !this.state.checked;
        const {checkedIdsList, data} = this.props;

        this.props.onChange(updateCheckedIdsList({checked, checkedIdsList, data}), data.id, checked);
    };

    getView = () => {
        const {
            data,
            data: {id, caption, matchedSearch = true},
            disabledNode,
            disabled,
            cssClasses,
            prefix
        } = this.props;
        const {checked, indeterminate} = this.state;

        const checkedState = indeterminate ? false : checked;

        const checkboxProps = {
            usePropsOnly: true,
            onChange: this.onChange,
            label: getLabel(prefix, caption),
            main: cssClasses.bold || cssClasses.boldNoPadding,
            checked: checkedState,
            indeterminate,
            disabled: disabled || disabledNode === id || !!data.disabled
        };

        if (this.props.view) {
            return this.props.view({
                checkboxProps,
                data
            });
        } else {
            return matchedSearch ? <CheckBox {...checkboxProps}/> : null;
        }
    };

    getAnchor = nodeView => ({anchorRef, getAnchorProps}) => (
        <span {...getAnchorProps({style: {display: 'inline-flex'}})} ref={anchorRef}>
            {nodeView}
        </span>
    );

    render() {
        const {
            data,
            disabled,
            showExplanations,
            layoutLevel
        } = this.props;
        const {
            caption,
            transient: transientFlag,
            explain,
            explanation,
            description,
            explainMaxWidth
        } = data;

        if (transientFlag) {
            return (
                <Label>
                    {caption}
                </Label>
            );
        }

        let view = this.getView();

        if (explain) {
            const mix = disabled ? 'tree__item_disabled-icon' : '';

            view = (
                <Info {...{
                    mix,
                    type: {popover: explain},
                    dropdownProps: {style: {zIndex: layoutLevel}},
                    inline: false,
                    maxWidth: explainMaxWidth
                }}>
                    {view}
                </Info>
            );
        }

        if (showExplanations && explanation) {
            const nodeView = view;

            view = (
                <Popover
                    hoverProps={{delay: 600}}
                    anchor={this.getAnchor(nodeView)}>
                    {description && <PopoverHeader>{description}</PopoverHeader>}
                    <div>{explanation}</div>
                </Popover>
            );
        }

        return view;
    }
}
